import { DateTime } from 'luxon';

export type ConsoleHistoryConnection =
  | { cpe_id: string; access_type: 'usb'; hw_id_path: string }
  | {
      cpe_id: string;
      access_type: 'eth';
      target_ip: string;
      target_port: number;
      protocol: string;
    };

export interface ConsoleHistoryCpe {
  cpe_id: string;
  access_type: 'eth';
}

export interface ConsoleHistoryItem {
  session_id: string;
  when: string;
  user: string;
  duration: number | null;
  elapsed_seconds: number | null;
  duration_label: string;
  status: 'active' | 'ended';
  access_type?: 'usb_console' | 'network_web' | 'raw_ssh';
  target?: { ip?: string | null; port?: number | null; protocol?: string | null } | null;
}

export interface ConsoleHistoryResponse {
  items: ConsoleHistoryItem[];
}

const SECONDS_PER_MINUTE = 60;
const SECONDS_PER_HOUR = 3600;
const MILLISECONDS_PER_SECOND = 1000;

export function historyDuration(item: ConsoleHistoryItem, fetchedAt: number, now: number): string {
  if (item.status !== 'active') return item.duration_label;
  const elapsed =
    Math.max(0, item.elapsed_seconds ?? 0) +
    Math.max(0, Math.floor((now - fetchedAt) / MILLISECONDS_PER_SECOND));
  const hours = Math.floor(elapsed / SECONDS_PER_HOUR);
  const minutes = Math.floor((elapsed % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const seconds = elapsed % SECONDS_PER_MINUTE;
  const parts: string[] = [];
  if (hours) parts.push(`${hours} hour${hours === 1 ? '' : 's'}`);
  if (hours || minutes) parts.push(`${minutes} minute${minutes === 1 ? '' : 's'}`);
  parts.push(`${seconds} second${seconds === 1 ? '' : 's'}`);
  return `${parts.join(' ')}(Active)`;
}

export function historyWhen(when: string): string {
  const date = DateTime.fromISO(when);
  return date.isValid ? date.toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS) : when;
}

export function historyType(item: ConsoleHistoryItem): string {
  const protocol = item.target?.protocol?.toLowerCase();
  if (protocol === 'http' || protocol === 'https' || protocol === 'ssh') return protocol;
  return item.access_type === 'raw_ssh' ? 'ssh' : 'N/A';
}

function csvCell(value: string): string {
  // Keep user-controlled names as text when the report is opened in a spreadsheet.
  const text = /^[\s]*[=+\-@]|^[\t\r\n]/.test(value) ? `'${value}` : value;
  return `"${text.replace(/"/g, '""')}"`;
}

export function historyCsv(
  items: ConsoleHistoryItem[],
  fetchedAt: number,
  now: number,
  includeNetworkDetails = false,
): string {
  const rows = [
    ['When', 'IAM User', ...(includeNetworkDetails ? ['Type', 'Target IP', 'Port'] : []), 'Duration'],
    ...items.map((item) => [
      historyWhen(item.when),
      item.user,
      ...(includeNetworkDetails
        ? [historyType(item), item.target?.ip || 'N/A', String(item.target?.port ?? 'N/A')]
        : []),
      historyDuration(item, fetchedAt, now),
    ]),
  ];
  return `\uFEFF${rows.map((row) => row.map(csvCell).join(',')).join('\r\n')}\r\n`;
}
